import type {BlindLevel, Chip} from '../../types/domain'

type ColorUpChip = Pick<Chip, 'value' | 'hide' | 'colorUp'>

function canPayExactly(amount: number, values: number[], index = 0): boolean {
  if (!Number.isSafeInteger(amount) || amount < 0) return false
  if (amount === 0) return true
  if (index === values.length) return false
  const value = values[index]
  if (index === values.length - 1) return amount % value === 0
  if (amount % value === 0) return true
  for (let remainder = amount; remainder > 0; remainder -= value) {
    if (canPayExactly(remainder, values, index + 1)) return true
  }
  return false
}

export function findChipsToColorUp(chips: ColorUpChip[] | null, blinds: BlindLevel[] | null, currentBlindNumber: number | null) {
  if (!chips?.length || !blinds?.length) return []
  const amounts = blinds.slice(currentBlindNumber ?? 0).flatMap(blind => [blind.smallBlind, blind.bigBlind, blind.ante ?? 0])
  let remaining = [...new Set(chips.filter(chip => !chip.hide && !chip.colorUp && Number.isSafeInteger(chip.value) && chip.value > 0).map(chip => chip.value))].sort((a, b) => a - b)
  const removed = new Set<number>()
  for (const value of remaining) {
    const candidate = remaining.filter(other => other !== value)
    if (!amounts.every(amount => canPayExactly(amount, candidate))) break
    removed.add(value)
    remaining = candidate
  }
  return chips.flatMap((chip, index) => !chip.hide && !chip.colorUp && removed.has(chip.value) ? [index] : []).reverse()
}

export function colorUp<T extends ColorUpChip>(chips: T[], blinds: BlindLevel[], currentBlindNumber: number, startingLevel = true): T[] {
  const chipsToColorUp = findChipsToColorUp(chips, blinds, currentBlindNumber)
  return chips.map((chip, index) => {
    if (chip.colorUp && startingLevel) return {...chip, hide: true}
    return chipsToColorUp.includes(index) ? {...chip, colorUp: true} : chip
  })
}
